import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Tag, ArrowRight } from 'lucide-react';
import ContestBanner from './ContestBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FADE_UP = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return ''; }
};

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/blog/posts`)
      .then((r) => r.ok ? r.json() : Promise.reject(`Server returned ${r.status}`))
      .then((data) => { if (!cancelled) setPosts(data.posts || []); })
      .catch((e) => { if (!cancelled) setErr(typeof e === 'string' ? e : 'Failed to load posts'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-['Outfit']">
      <ContestBanner />

      <div className="max-w-4xl mx-auto px-6 pt-6">
        <a
          href="/"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF5E1A] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to site
        </a>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-10 pb-12">
        <motion.div {...FADE_UP} className="mb-6">
          <div className="text-xs font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">
            // Field notes from the driveway
          </div>
          <h1 className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-4">
            The AI Tuner Pro<br />
            <span className="bg-gradient-to-r from-[#FF5E1A] via-[#FF9A42] to-[#FFD466] bg-clip-text text-transparent">
              Blog.
            </span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl">
            Real tuning guides written from real datalogs. No hype. No filler. No database tunes.
          </p>
        </motion.div>
      </section>

      {/* Posts */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        {loading && (
          <div className="text-zinc-500 text-sm font-['JetBrains_Mono']" data-testid="blog-loading">Loading posts…</div>
        )}
        {err && (
          <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm">{err}</div>
        )}
        {!loading && !err && posts.length === 0 && (
          <div className="text-zinc-500 text-sm" data-testid="blog-empty">
            No posts published yet. Check back soon — new tuning guides are added every couple of weeks.
          </div>
        )}

        <div className="space-y-4" data-testid="blog-list">
          {posts.map((p) => (
            <motion.article
              {...FADE_UP}
              key={p.slug}
              data-testid={`blog-card-${p.slug}`}
              className="group"
            >
              <a
                href={`/blog/${p.slug}`}
                className="block p-6 bg-[#111111] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/50 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-center gap-3 text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(p.published_at)}</span>
                  <span className="text-zinc-700">·</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> {p.read_minutes || 5} min read</span>
                </div>
                <h2 className="font-bold text-xl sm:text-2xl text-white group-hover:text-[#FF5E1A] transition-colors mb-2 leading-tight">
                  {p.title}
                </h2>
                <p className="text-sm text-zinc-400 mb-4 leading-relaxed">{p.excerpt}</p>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex flex-wrap gap-1.5">
                    {(p.tags || []).slice(0, 4).map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-400">
                        <Tag className="w-2.5 h-2.5" /> {t}
                      </span>
                    ))}
                  </div>
                  <span className="text-xs text-[#FF5E1A] font-bold tracking-wider uppercase flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </a>
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
}
